import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Calendar,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Radio,
  Typography,
} from "fds";
import "./index.scss";
import { Workgroup } from "../../types/workgroup";
import dayjs from "dayjs";
import { ManipulateType } from "dayjs";
import utilsCommon from "../../shared/utils/common";
import { permissionDTO } from "../../types/auth";
import { t } from "i18next";

interface WorkgroupInfoSettingTabProps {
  workgroupInfo?: Workgroup;
  changeWorkgroupInfo: (newWorkgroup: Workgroup) => void;
  changeWorkgroupPolicy: (newPolicy: permissionDTO) => void;
  policy?: permissionDTO;
  isWorkgroupManager?: boolean;
}
const invite_policy_type = [
  { key: 0, resource: t("workgroup.onlyWorkgroupHead") },
  { key: 2, resource: t("workgroup.onlyInternal") },
  { key: 1, resource: t("workgroup.allMembers") },
];
export const expireType_policy_type = [
  { key: "-1", resource: t("file.policy.noDeadline") },
  { key: "1_month", resource: t("common.period.oneMonth") },
  { key: "6_month", resource: t("common.period.sixMonth") },
  { key: "1_year", resource: t("common.period.oneYear") },
  { key: "3_year", resource: t("common.period.threeYear") },
  { key: "custom", resource: t("common.period.userRangeShort") },
];
const file_share_policy_type = [
  { key: "WEBUSING", resource: t("file.onlyPreview") },
  { key: "DOWNLOAD", resource: t("file.onlyDownload") },
  { key: "WEBUSING_AND_DOWNLOAD", resource: t("file.previewNDownload") },
];
const WorkgroupInfoSettingTab: React.FC<WorkgroupInfoSettingTabProps> = ({
  workgroupInfo,
  policy,
  isWorkgroupManager,
  changeWorkgroupInfo,
  changeWorkgroupPolicy,
}) => {
  const date = new Date();
  const serverTimezone = -date.getTimezoneOffset() / 60;

  /** 지정된 날짜와 동일한지 확인하는 함수 */
  const checkExactDayDiff = (
    expireDate: string | number,
    updateDate: number,
    type: string
  ) => {
    const numValue = parseInt(type.split("_")[0]);
    const strValue = type.split("_")[1] as ManipulateType;
    let calcedExpiredDate = dayjs(expireDate).startOf("day");
    calcedExpiredDate = calcedExpiredDate.subtract(numValue, strValue);
    return (
      dayjs(updateDate).startOf("day").valueOf() === calcedExpiredDate.valueOf()
    );
  };
  /** State정리 */
  const [buttonClick, changeButtonClick] = useState(0); // 드롭다운 클릭용
  const [expireDateFilter, changeExpireDateFilter] = useState(
    workgroupInfo?.endDate === -1
      ? "-1"
      : expireType_policy_type.filter(
          (item) =>
            item?.key !== "-1" &&
            checkExactDayDiff(
              workgroupInfo?.endDate ?? 0,
              workgroupInfo?.updateDate ?? 0,
              item?.key
            )
        )[0]?.key ?? "custom"
  ); //유효기간 계산용
  /** 함수 정리 */

  /** 각 카테고리 별로, 알맞는 값 표시 */
  const renderPolicyByType = (
    type: "invitePolicy" | "expireType" | "shareLink",
    value: any
  ) => {
    if (type === "invitePolicy") {
      switch (value) {
        case 0:
          return t("workgroup.onlyWorkgroupHead");
        case 1:
          return t("workgroup.allMembers");
        default:
          return t("workgroup.onlyInternal");
      }
    } else if (type === "expireType") {
      if (value === -1) {
        return t("file.policy.noDeadline");
      } else {
        return t("workgroup.until", {
          date: utilsCommon.renderShortDateFormat(workgroupInfo?.endDate),
        });
      }
    } else if (type === "shareLink") {
      if (value.authType !== "NONE") {
        return t("common.verified");
      }
      let urlAction =
        value.urlAction === "WEBUSING_AND_DOWNLOAD"
          ? t("file.previewNDownload")
          : value.urlAction === "WEBUSING"
          ? t("file.onlyPreview")
          : t("file.onlyDownload");
      return (
        `${t("common.unVerified")} (${urlAction})` +
        (value.allowDays <= -1
          ? ""
          : `, ${t("file.expireAfter", {
              day: value.allowDays,
            })}`)
      );
    }
  };
  /** UI: 워크그룹 일반 설정 - 일반 사용자 화면 */
  const renderUserMode = () => {
    return (
      <Box direction="column">
        <Typography type="subtitle2">{t("common.general")}</Typography>
        <div className="workgroupModal-body--header" />
        <Box classes="mg mt-20 workgroupModal-body--items">
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("workgroup.invitation")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 140px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyByType("invitePolicy", workgroupInfo?.invitePolicy)}
            </Typography>
          </Box>
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("workgroup.expiration")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 140px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyByType("expireType", workgroupInfo?.endDate)}
            </Typography>
          </Box>
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("file.linkTitle")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 140px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyByType(
                "shareLink",
                policy?.policy?.fileShareLinkPolicy
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  /** 유효기간 설정 */
  const handleWorkgroupExpireDate = (key: string) => {
    changeExpireDateFilter(key);
    changeButtonClick(buttonClick + 1);
    if (key === "custom") {
      workgroupInfo &&
        changeWorkgroupInfo({
          ...workgroupInfo,
          endDate: dayjs().endOf("day").valueOf(),
        });
    } else if (key !== "-1") {
      const numValue = parseInt(key.split("_")[0]);
      const strValue = key.split("_")[1] as ManipulateType;
      workgroupInfo &&
        changeWorkgroupInfo({
          ...workgroupInfo,
          endDate: dayjs().add(numValue, strValue).endOf("day").valueOf(),
        });
    } else {
      workgroupInfo &&
        changeWorkgroupInfo({
          ...workgroupInfo,
          endDate: -1,
        });
    }
  };
  /** 공유 링크 설정 */
  const handleWorkgroupFileshareLinkPolicy = (
    key: string,
    value: string | number
  ) => {
    policy?.policy &&
      policy?.policy.fileShareLinkPolicy &&
      changeWorkgroupPolicy({
        ...policy,
        policy: {
          ...policy?.policy,
          fileShareLinkPolicy: {
            ...policy?.policy.fileShareLinkPolicy,
            allowDaysTimeZone: serverTimezone,
            [key]: value,
          },
        },
      });
    changeButtonClick(buttonClick + 1);
  };
  /** UI: 워크그룹 일반 설정 - 워크그룹장 화면 */
  const renderMasterMode = () => {
    return (
      <Box direction="column">
        <Typography type="subtitle2">{t("workgroup.invitation")}</Typography>
        <Dropdown
          placeholder="s"
          value={renderPolicyByType(
            "invitePolicy",
            workgroupInfo?.invitePolicy
          )}
          width={300}
          buttonWidth={300}
          classes="mg mt-6"
          buttonClick={buttonClick}
        >
          {invite_policy_type.map((item) => (
            <DropdownItem
              label={item.resource}
              key={item?.key}
              type="check"
              checkColor={workgroupInfo?.invitePolicy === item?.key}
              checked={workgroupInfo?.invitePolicy === item?.key}
              onClick={() => {
                workgroupInfo &&
                  changeWorkgroupInfo({
                    ...workgroupInfo,
                    invitePolicy: item?.key,
                  });
                changeButtonClick(buttonClick + 1);
              }}
            />
          ))}
        </Dropdown>
        <Typography type="subtitle2" classes="mg mt-32">
          {t("workgroup.expiration")}
        </Typography>
        <Typography
          type="body4_rg"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
          whiteSpace="break-spaces"
        >
          {t("workgroup.expirationDesc")}
        </Typography>
        <Dropdown
          placeholder={t("common.period.userRangeShort")}
          value={
            expireType_policy_type.filter(
              (item) => item?.key === expireDateFilter
            )[0]?.resource
          }
          width={300}
          buttonWidth={300}
          classes="mg mt-6"
          buttonClick={buttonClick}
        >
          {expireType_policy_type.map((item) => (
            <DropdownItem
              label={item.resource}
              key={item?.key}
              type="check"
              checkColor={expireDateFilter === item?.key}
              checked={expireDateFilter === item?.key}
              onClick={() => handleWorkgroupExpireDate(item?.key)}
            />
          ))}
        </Dropdown>
        {expireDateFilter === "custom" ? (
          <Box alignItems="center" classes="mg mt-8 mb-22">
            <Calendar
              type={"single"}
              placeholder={t("common.selectDate")}
              width={200}
              selected={
                workgroupInfo?.endDate
                  ? new Date(workgroupInfo?.endDate)
                  : undefined
              }
              minDate={new Date()}
              onSubmit={(date) =>
                workgroupInfo &&
                changeWorkgroupInfo({
                  ...workgroupInfo,
                  endDate: dayjs(date instanceof Date ? date : "")
                    .endOf("day")
                    .valueOf(),
                })
              }
            />
            <Typography type="body2_rg" classes="mg ml-8 ">
              {t("workgroup.untilNoDate")}
            </Typography>
          </Box>
        ) : (
          expireDateFilter !== "-1" && (
            <Box alignItems="center" classes="mg mt-6">
              <Icons variant="line" label="info" size="20px" />
              <Typography type="body2_rg" classes="mg ml-2">
                {t("workgroup.until", {
                  date: utilsCommon.renderShortDateFormat(
                    workgroupInfo?.endDate
                  ),
                })}
              </Typography>
            </Box>
          )
        )}
        {!window.eco.isOnlyVerifiedLinkAllowed && (
          <Box direction="column">
            <Typography type="subtitle2" classes="mg mt-32">
              {t("file.linkTitle")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.linkDesc")}
            </Typography>
            <Box width={"100%"} classes="mg mt-12">
              <Radio
                label={t("common.unVerified")}
                check={
                  policy?.policy?.fileShareLinkPolicy?.authType !== "LOGIN"
                }
                style={{ width: "50%", justifyContent: "flex-start" }}
                onClick={() =>
                  handleWorkgroupFileshareLinkPolicy("authType", "NONE")
                }
              />
              <Radio
                label={t("common.verified")}
                check={
                  policy?.policy?.fileShareLinkPolicy?.authType === "LOGIN"
                }
                style={{ width: "50%", justifyContent: "flex-start" }}
                onClick={() =>
                  handleWorkgroupFileshareLinkPolicy("authType", "LOGIN")
                }
              />
            </Box>
            {policy?.policy?.fileShareLinkPolicy?.authType !== "LOGIN" && (
              <Box
                classes="mg mt-12 ml-28"
                direction="column"
                alignItems="flex-start"
              >
                <Dropdown
                  placeholder=" "
                  value={
                    file_share_policy_type.filter(
                      (item) =>
                        item?.key ===
                        policy?.policy?.fileShareLinkPolicy?.urlAction
                    )[0]?.resource
                  }
                  positionStrategy="fixed"
                  width={180}
                  buttonClick={buttonClick}
                >
                  {file_share_policy_type.map((item) => (
                    <DropdownItem
                      label={item.resource}
                      key={item?.key}
                      checked={
                        item?.key ===
                        policy?.policy?.fileShareLinkPolicy?.urlAction
                      }
                      checkColor={
                        item?.key ===
                        policy?.policy?.fileShareLinkPolicy?.urlAction
                      }
                      type="check"
                      onClick={() =>
                        handleWorkgroupFileshareLinkPolicy(
                          "urlAction",
                          item?.key
                        )
                      }
                    />
                  ))}
                </Dropdown>
                <Checkbox
                  classDiv="mg mt-12"
                  label={t("file.setEndDate")}
                  check={
                    !policy?.policy?.fileShareLinkPolicy?.allowDays ||
                    policy?.policy?.fileShareLinkPolicy?.allowDays > -1
                  }
                  onClick={() =>
                    handleWorkgroupFileshareLinkPolicy(
                      "allowDays",
                      policy?.policy?.fileShareLinkPolicy?.allowDays &&
                        policy?.policy?.fileShareLinkPolicy?.allowDays <= -1
                        ? 60
                        : -2
                    )
                  }
                />
                {(!policy?.policy?.fileShareLinkPolicy?.allowDays ||
                  policy?.policy?.fileShareLinkPolicy?.allowDays > -1) && (
                  <Box
                    alignItems="center"
                    classes="workgroupInfoSetting-linkExpireDate"
                  >
                    <InputField
                      placeholder={""}
                      size="md"
                      width="100px"
                      type="positive-integer"
                      value={
                        policy?.policy?.fileShareLinkPolicy?.allowDays + ""
                      }
                      onChange={(e) =>
                        handleWorkgroupFileshareLinkPolicy(
                          "allowDays",
                          e.target.value ? parseInt(e.target.value) : ""
                        )
                      }
                      maxLength={3}
                    />
                    <Typography
                      classes="mg ml-12"
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_PRIMARY")}
                    >
                      {t("file.expireAfterDays")}
                    </Typography>
                    <Typography
                      classes="mg ml-2"
                      type="body4_rg"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {t("file.dependUpload")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };
  return isWorkgroupManager ? renderMasterMode() : renderUserMode();
};
export default WorkgroupInfoSettingTab;
